import { Portal } from '@ark-ui/react/portal';
import { createListCollection, Select } from '@ark-ui/react/select';
import { Check, ChevronDown } from 'lucide-react';
import { type FC, type RefObject, useMemo } from 'react';

export interface DialogSelectOption {
  label: string;
  /** Inline SVG source of an agent mark, drawn before the label. */
  mark?: string;
  value: string;
}

/*
 * SVG source strings, so dangerouslySetInnerHTML is the only way in: an <img
 * src> would drop the .ds-agent-logo classes. data-ds-agent-logo-inline
 * supplies the invert colour the glyphs paint with. The utility takes the
 * class's 40px down to 20, and outranks it on layer order.
 */
const AgentMark: FC<{ mark: string }> = ({ mark }) => (
  <span
    aria-hidden="true"
    className="flex shrink-0 items-center [&_.ds-agent-logo]:size-[var(--ds-space-5)]"
    dangerouslySetInnerHTML={{ __html: mark }}
    data-ds-agent-logo-inline
  />
);

interface DialogSelectProps {
  /**
   * Where the menu is portaled to. It must be an element inside the dialog —
   * see the note below — and outside whatever scrolls, so the menu is neither
   * hidden nor clipped.
   */
  container: RefObject<HTMLElement | null>;
  disabled?: boolean;
  /** The control's accessible name. Clipped to a 2px box, as the DS draws it. */
  label: string;
  onValueChange: (value: string) => void;
  options: DialogSelectOption[];
  /** Shown in the trigger while `value` is null. */
  placeholder?: string;
  value: string | null;
}

/**
 * A dropdown for use inside an Ark Dialog: Ark's Select wearing the design
 * system's own `data-ds-select-*` hooks, so forms/Select.css styles it exactly
 * as it styles the component.
 *
 * Drawn by the invite panel's Role field and the feedback dialog's agent field.
 *
 * ## Why not `<Select>` from @aecom-ai-engineering/react-components
 *
 * That component hardcodes `<Portal>` with no container, so its menu mounts as
 * a direct child of `<body>`. Ark's Dialog, one animation frame after it opens,
 * calls `hideOthers` on the content element — which puts `aria-hidden="true"`
 * on every other child of `<body>`, the menu's positioner included. The menu
 * still renders and still works with a mouse, and it is invisible to a screen
 * reader: the options are not in the accessibility tree at all.
 *
 * The positioner already exists by then, because the DS does not lazy-mount it,
 * so this is the ordering every time rather than a race.
 *
 * Portaling into the dialog's own content element puts the menu inside the one
 * branch `hideOthers` leaves alone. The content element and not the scrolling
 * body: a menu inside the scroll container is clipped at its bottom edge.
 *
 * The standing ask upstream is a `container` prop on the design system's
 * Select, or a lazily-mounted positioner. Either would retire this file.
 *
 * Every attribute below is the one the DS puts on that part — read off its
 * build, and the reason this is a reproduction rather than a second design.
 */
export const DialogSelect: FC<DialogSelectProps> = ({
  container,
  disabled = false,
  label,
  onValueChange,
  options,
  placeholder,
  value,
}) => {
  const selectedMark = options.find((option) => option.value === value)?.mark;

  const collection = useMemo(
    () =>
      createListCollection({
        items: options,
        itemToString: (item) => item.label,
        itemToValue: (item) => item.value,
      }),
    [options],
  );

  return (
    <Select.Root
      // Both frames run the field the full width of the dialog body. Select.css
      // caps the root at `min(100%, var(--ds-space-52))` — 208px, the width the
      // project toolbar's dropdown wants — so the cap has to be lifted here.
      //
      // A plain utility rather than a rule in a stylesheet: Tailwind
      // sorts layer(utilities) after layer(components), so this outranks the
      // library without naming a data-ds-* selector, which is what
      // check-css-layers.mjs is there to refuse.
      className="w-full"
      collection={collection}
      data-ds-select=""
      disabled={disabled}
      onValueChange={({ value: next }) => {
        const [first] = next;
        if (first !== undefined) onValueChange(first);
      }}
      value={value === null ? [] : [value]}
    >
      <Select.Label data-ds-select-label="">{label}</Select.Label>
      <Select.Control data-ds-select-control="">
        <Select.Trigger data-ds-select-trigger="">
          <span data-ds-select-trigger-content="">
            {selectedMark !== undefined && <AgentMark mark={selectedMark} />}
            <Select.ValueText data-ds-select-value="" placeholder={placeholder} />
          </span>
          {/* The frame's plain chevron-down, which is the DS's `single`
              indicator. Select.css rotates it on [data-state="open"]. */}
          <Select.Indicator data-ds-select-indicator="">
            <ChevronDown aria-hidden="true" />
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>

      <Portal container={container}>
        <Select.Positioner data-ds-select-positioner="" data-menu-width="trigger">
          <Select.Content data-ds-select-content="">
            <Select.ItemGroup data-ds-select-group="">
              {options.map((option) => (
                <Select.Item data-ds-select-item="" item={option} key={option.value}>
                  {option.mark === undefined ? (
                    <>
                      <Select.ItemIndicator data-ds-select-item-check="">
                        <Check aria-hidden="true" />
                      </Select.ItemIndicator>
                      <Select.ItemText data-ds-select-item-text="">{option.label}</Select.ItemText>
                    </>
                  ) : (
                    // The mark stays on the picked row too, so the tick moves
                    // to the end instead of taking the mark's place.
                    <>
                      <AgentMark mark={option.mark} />
                      <Select.ItemText data-ds-select-item-text="">{option.label}</Select.ItemText>
                      <Select.ItemIndicator data-ds-select-item-check="">
                        <Check aria-hidden="true" />
                      </Select.ItemIndicator>
                    </>
                  )}
                </Select.Item>
              ))}
            </Select.ItemGroup>
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
};
